// Il "protocollo" dei campi di una pratica: com'è fatto un descrittore di campo,
// dove si posiziona sulla griglia, quando si considera vuoto e come si sfogliano
// le righe di un gruppo ripetibile.
//
// Sta qui, e non nel dominio, perché lo leggono in due: il renderer generico
// (`ProcessPanel`, che non deve sapere nulla di flotta) e l'editor dei tipi
// (feature). Duplicarlo significherebbe farli divergere al primo tipo nuovo.
//
// Il *catalogo* dei tipi disponibili resta invece lato feature
// (`processes/logic/processTypesLogic`): è una scelta di prodotto, non un
// contratto di rendering.

export const GRID_COLUMNS = 12;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toInt = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
};

/** I blocchi (sezione, gruppo) occupano di default tutta la riga. */
const isBlock = (field) => field?.type === 'section' || field?.type === 'group';

/** Layout di un campo, completato coi default e limitato alla griglia. */
export function layoutOf(field, index = 0) {
  const raw = field?.layout || {};
  const w = clamp(toInt(raw.w, isBlock(field) ? GRID_COLUMNS : GRID_COLUMNS / 2), 1, GRID_COLUMNS);
  const x = clamp(toInt(raw.x, 0), 0, GRID_COLUMNS - w);
  const y = Math.max(0, toInt(raw.y, index));
  return { x, y, w };
}

/** Stile CSS grid di un campo — l'unico punto che traduce il modello in CSS. */
export const gridStyleOf = (field, index = 0) => {
  const { x, y, w } = layoutOf(field, index);
  return { gridColumn: `${x + 1} / span ${w}`, gridRow: y + 1 };
};

const overlaps = (a, b) => a.y === b.y && a.x < b.x + b.w && b.x < a.x + a.w;

/**
 * Rende il layout di un elenco di campi valido e leggibile:
 *
 * 1. completa e limita i valori mancanti o fuori scala;
 * 2. scioglie le sovrapposizioni spingendo in basso chi arriva dopo (l'ordine è
 *    per riga e poi per colonna, cioè come si legge);
 * 3. compatta verso l'alto, così un buco lasciato da un campo spostato non
 *    resta come riga vuota.
 *
 * Sta qui, accanto al modello di posizione, perché serve a **due** chiamanti: la
 * pagina che compila la pratica (dove due elenchi di campi — quelli comuni e
 * quelli dello stage — possono dichiarare le stesse coordinate) e l'editor dei
 * tipi, che la usa dopo ogni spostamento. Senza questo passaggio due campi
 * finiscono nella stessa cella e le etichette si sovrascrivono.
 *
 * Non muta l'input: restituisce nuovi campi, ordinati come vanno letti.
 */
export function normalizeLayout(fields = []) {
  const items = fields
    .map((field, index) => ({ field, layout: layoutOf(field, index) }))
    .sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x);

  const placed = [];
  for (const item of items) {
    const layout = { ...item.layout };
    while (placed.some((p) => overlaps(p.layout, layout))) layout.y += 1;
    placed.push({ ...item, layout });
  }

  // Compattazione: ogni campo sale finché trova posto, nell'ordine di lettura.
  for (const item of placed) {
    while (item.layout.y > 0) {
      const candidate = { ...item.layout, y: item.layout.y - 1 };
      const blocked = placed.some((p) => p !== item && overlaps(p.layout, candidate));
      if (blocked) break;
      item.layout = candidate;
    }
  }

  return placed
    .sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x)
    .map((item) => ({ ...item.field, layout: item.layout }));
}

/** Numero di righe occupate — serve all'editor per disegnare la griglia. */
export const layoutRowCount = (fields = []) =>
  fields.reduce((max, field, index) => Math.max(max, layoutOf(field, index).y + 1), 0);

/**
 * Se un campo obbligatorio è da considerarsi non compilato.
 *
 * Ogni tipo ha una nozione diversa di "vuoto": un checkbox obbligatorio va
 * spuntato, una scelta multipla vuole almeno un valore, un gruppo a righe fisse
 * vuole ogni riga completa nei suoi sotto-campi obbligatori — altrimenti
 * basterebbe compilare la prima gomma per passare oltre.
 */
export function isFieldEmpty(field, value) {
  switch (field?.type) {
    case 'checkbox':
      return value !== true;
    case 'multiselect':
    case 'photo':
      return !Array.isArray(value) || value.length === 0;
    case 'group':
      return isGroupEmpty(field, value);
    default:
      return value === undefined || value === null || String(value).trim() === '';
  }
}

function isGroupEmpty(field, value) {
  const rows = Array.isArray(value) ? value : [];
  const required = (field.fields || []).filter((f) => f.required);

  // Righe fisse: devono esserci tutte, ognuna completa.
  if ((field.rows || []).length) {
    return (field.rows || []).some((row) => {
      const filled = rows.find((r) => r?._row === row.key);
      if (!filled) return true;
      return required.some((sub) => isFieldEmpty(sub, filled[sub.key]));
    });
  }

  // Righe libere: almeno una, e tutte complete nei sotto-campi obbligatori.
  if (!rows.length) return true;
  return hasIncompleteRows(field, rows);
}

/**
 * Se un gruppo a righe libere ha righe aggiunte ma lasciate a metà.
 *
 * Vale anche per un gruppo facoltativo: la tabella danni può restare vuota, ma
 * un danno aggiunto senza foto o senza titolo non è un danno segnalato.
 */
export function hasIncompleteRows(field, value) {
  if (field?.type !== 'group' || (field.rows || []).length) return false;
  const rows = Array.isArray(value) ? value : [];
  const required = (field.fields || []).filter((f) => f.required);
  return rows.some((row) => required.some((sub) => isFieldEmpty(sub, row?.[sub.key])));
}

/**
 * Righe da mostrare per un gruppo.
 *
 * Le righe fisse esistono anche prima di essere compilate — sono l'elenco da
 * spuntare (le quattro gomme), non il risultato della compilazione — quindi
 * vengono restituite comunque, con i valori trovati o un guscio vuoto.
 */
export function groupRows(field, value) {
  const rows = Array.isArray(value) ? value : [];
  if ((field?.rows || []).length) {
    return field.rows.map((row) => ({
      key: row.key,
      label: row.label || row.key,
      fixed: true,
      values: rows.find((r) => r?._row === row.key) || { _row: row.key },
    }));
  }
  return rows.map((values, index) => ({
    key: values?._row || `row_${index}`,
    label: '',
    fixed: false,
    values,
  }));
}
